import React, { useEffect, useState } from "react";
import { Tabs, notification, Empty } from "antd";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import {
  _single_business_menu,
  _get_categories,
} from "../../common/axios_services";
import { FoodCard } from "./FoodCard";

const { TabPane } = Tabs;

export const ImageTray = (props) => {
  const businessType = props.businessType;

  const TabContent = ({ items }) => {
    const filteredItems = items || [];

    if (filteredItems.length === 0) {
      return <Empty description="No products available in this category." className="my-12" />;
    }

    return (
      <div className="grid xl:grid-cols-4 lg:grid-cols-3 grid-cols-2 gap-y-16 gap-6 mb-36">
        {filteredItems.map((item, index) => (
          <FoodCard
            key={index}
            item={item}
            businessType={businessType}
            isOpen={props.isOpen}
            openTime={props.openTime}
            closeTime={props.closeTime}
          />
        ))}
      </div>
    );
  };

  const { id: restaurantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");

  const [loading, setLoading] = useState(true);
  const [restaurantProducts, setRestaurantProducts] = useState([]);
  const [categoryTags, setCategoryTags] = useState([]);
  const [activeDeliveryMode, setActiveDeliveryMode] = useState(
    initialTab === "next_day" ? "next_day" : "same_day"
  );

  const openNotificationWithIcon = (type, message) => {
    notification[type]({
      message: "",
      description: message,
    });
  };

  const handleModeSwitch = (mode) => {
    setActiveDeliveryMode(mode);
    const newParams = new URLSearchParams(location.search);
    newParams.set("tab", mode);
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const getCategories = async () => {
    try {
      setLoading(true);
      const categories = await _get_categories();
      setCategoryTags(categories.data.data);
    } catch (err) {
      openNotificationWithIcon(
        "error",
        err.response?.data?.message || err.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchMenu = async (id) => {
    setLoading(true);
    try {
      const response = await _single_business_menu({
        page: 1,
        per_page: 500,
        businessId: id,
        type: businessType,
      });
      const menuData = response?.data?.data || [];
      setRestaurantProducts(menuData);

      if (businessType === "restaurant") {
        const sameDayCount = menuData.filter(
          (i) => (i.deliveryDateType || "same_day") === "same_day",
        ).length;
        const nextDayCount = menuData.filter(
          (i) => i.deliveryDateType === "next_day",
        ).length;

        if (!initialTab) {
          const defaultMode = sameDayCount === 0 && nextDayCount > 0 ? "next_day" : "same_day";
          setActiveDeliveryMode(defaultMode);
          const newParams = new URLSearchParams(location.search);
          newParams.set("tab", defaultMode);
          navigate({ search: newParams.toString() }, { replace: true });
        }
      }
    } catch (err) {
      console.error("Error fetching menu:", err);
      openNotificationWithIcon(
        "error",
        err?.response?.data?.message ??
          "An error occurred while fetching product data. Please reload page to try again",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantId) {
      fetchMenu(restaurantId);
    }
  }, [restaurantId, categoryTags]);

  useEffect(() => {
    getCategories();
  }, []);

  if (loading) return <p>Loading menu...</p>;

  const isRestaurant = businessType === "restaurant";

  const sameDayItems = isRestaurant
    ? restaurantProducts.filter(
        (i) => (i.deliveryDateType || "same_day") === "same_day",
      )
    : restaurantProducts;

  const nextDayItems = isRestaurant
    ? restaurantProducts.filter((i) => i.deliveryDateType === "next_day")
    : [];

  const currentModeItems = isRestaurant
    ? activeDeliveryMode === "same_day"
      ? sameDayItems
      : nextDayItems
    : restaurantProducts;

  const categorizedData = {
    All: currentModeItems,
  };

  const validCategories = currentModeItems
    .map((item) => item?.category)
    .filter((cat) => cat && (cat.id || cat.name));

  const uniqueCategories = [
    ...new Map(
      validCategories.map((cat) => [cat.id || cat.name, cat]),
    ).values(),
  ];

  uniqueCategories.forEach((category) => {
    const catName = category.name || category;
    const catId = category.id;
    categorizedData[catName] = currentModeItems.filter((item) => {
      if (!item?.category) return false;
      if (catId && item.category.id) return item.category.id === catId;
      return item.category.name === catName || item.category === catName;
    });
  });

  if (restaurantProducts.length === 0)
    return <Empty description="No menu available." />;

  return (
    <div>
      {isRestaurant && (
        <div className="flex flex-wrap gap-3 mb-8 bg-gray-100 p-2 rounded-3xl w-fit border border-gray-200">
          <button
            onClick={() => handleModeSwitch("same_day")}
            className={`px-8 py-4 rounded-2xl font-bold text-lg md:text-xl transition-all cursor-pointer ${
              activeDeliveryMode === "same_day"
                ? "bg-black text-white shadow-md scale-[1.02]"
                : "text-gray-700 hover:text-black hover:bg-gray-200/60"
            }`}
          >
            Same Day Menu ({sameDayItems.length})
          </button>
          <button
            onClick={() => handleModeSwitch("next_day")}
            className={`px-8 py-4 rounded-2xl font-bold text-lg md:text-xl transition-all cursor-pointer ${
              activeDeliveryMode === "next_day"
                ? "bg-black text-white shadow-md scale-[1.02]"
                : "text-gray-700 hover:text-black hover:bg-gray-200/60"
            }`}
          >
            Next Day Menu ({nextDayItems.length})
          </button>
        </div>
      )}

      {currentModeItems.length === 0 ? (
        <Empty
          description={
            activeDeliveryMode === "same_day"
              ? "No Same Day items available for this restaurant."
              : "No Next Day items available for this restaurant."
          }
          className="my-12"
        />
      ) : (
        <Tabs defaultActiveKey="All">
          {Object.keys(categorizedData).map((key) => (
            <TabPane tab={key} key={key}>
              <TabContent items={categorizedData[key]} />
            </TabPane>
          ))}
        </Tabs>
      )}
    </div>
  );
};
